const firebaseConfig = {
	apiKey: import.meta.env.VITE_FIREBASE_APIKEY,
	authDomain: import.meta.env.VITE_FIREBASE_AUTHDOMAIN,
	databaseURL: import.meta.env.VITE_FIREBASE_DATABASEURL,
	projectId: import.meta.env.VITE_FIREBASE_PROJECTID,
	storageBucket: import.meta.env.VITE_FIREBASE_STORAGEBUCKET,
	messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGINGSENDERID,
	appId: import.meta.env.VITE_FIREBASE_APPID,
}


import { initializeApp, getApps } from 'firebase/app'
import { initializeFirestore, getDocs, getDoc, addDoc, deleteDoc, collection, doc, connectFirestoreEmulator, setDoc, query, where, serverTimestamp, writeBatch, documentId, } from 'firebase/firestore'
import { connectAuthEmulator, getAuth } from 'firebase/auth'
import { connectDatabaseEmulator, getDatabase } from 'firebase/database'
import { initializeAppCheck, getToken, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import { v4 } from 'uuid'
import { unproxy } from '@/lib/utils'

class FB {

	static instance

	constructor() {
		const apps = getApps()
		if (!apps.length) {
			const app = initializeApp(firebaseConfig)

			this.auth = getAuth()
			this.firestore = initializeFirestore(app, {ignoreUndefinedProperties: true, })
			// this.firestore.settings()
			this.database = getDatabase()

			if (import.meta.env.DEV) {
				connectAuthEmulator(this.auth, 'http://127.0.0.1:5075', {disableWarnings: true})
				connectDatabaseEmulator(this.database, '127.0.0.1', 5073)
				connectFirestoreEmulator(this.firestore, '127.0.0.1', 5071)
			}

			const provider =  new ReCaptchaEnterpriseProvider(import.meta.env.VITE_RECAPTCHA_KEY)
			this.appCheck = initializeAppCheck(app, {provider, isTokenAutoRefreshEnabled: true })
		}
	}

	static getInstance() {
		if (!FB.instance) {
			FB.instance = new FB()
		}
		return FB.instance
	}

	async appHeaders(refresh = false) {
		const appToken = await getToken(this.appCheck, refresh)

		return {
			'X-Firebase-AppCheck': appToken.token
		}
	}

	async getDocs(path) {
		const collectionRef = collection(this.firestore, path)
		const snapshot = await getDocs(collectionRef)
		const results = []
		snapshot.forEach(doc => {
			const data = doc.data()
			data.createdAt = data.createdAt?.toDate()
			data.updatedAt = data.updatedAt?.toDate()
			results.push({
				$id: doc.id ?? v4(),
				// id: doc.id,
				...data,
			})
		})
		return results
	}

	async getDoc(path) {
		const docRef = doc(this.firestore, path)
		const docSnap = await getDoc(docRef)
		const data = docSnap.data() || {}
		data.createdAt = data?.createdAt?.toDate()
		data.updatedAt = data?.updatedAt?.toDate()

		return {
			$id: docSnap.id,
			...data
		}
	}

	async removeDoc(path) {
		const docRef = doc(this.firestore, path)
		await deleteDoc(docRef)

		return path
	}

	async removeCollection(path) {
		const collectionRef = collection(this.firestore, path)
		const querySnapshot = await getDocs(query(collectionRef))
		querySnapshot.forEach(async (doc) => {
			console.log('REMOVE.DOC', doc)
			try {
				await deleteDoc(doc.ref)
			} catch (error) {
				console.log('deleteDOC.WTF', error)
			}
		})

		return path
	}

	async saveDoc(path, data) {
		const docRef = doc(this.firestore, path)
		data = unproxy(data)
		const id = data.$id
		delete data.$id

		data.createdAt = data.createdAt && typeof data.createdAt == 'string' ?  new Date(data.createdAt) : serverTimestamp()
		data.updatedAt = serverTimestamp()

		console.log('saveDoc.data', data)
		await setDoc(docRef, data)
		return {
			$id: id,
			...data,
		}
	}

	async updateDoc(path, data) {
		const docRef = doc(this.firestore, path)
		data = unproxy(data)
		const id = data.$id
		delete data.$id
		data.createdAt = data.createdAt ?  new Date(data.createdAt) : serverTimestamp()
		data.updatedAt = serverTimestamp()
		await setDoc(docRef, data, { merge: true})
		return {
			$id: id,
			...data,
		}
	}

	async addDoc(path, data) {
		const docRef = collection(this.firestore, path)
		data = unproxy(data)
		data.createdAt = serverTimestamp()
		const res = await addDoc(docRef, data)
		return {
			$id: res.id,
			...data
		}
	}

	async doQuery(path, wheres) {
		const whereClauses = wheres.map(w => where(...w))

		const q = query( collection(this.firestore, path), ...whereClauses)
		const snapshot = await getDocs(q)
		const results = []
		snapshot.forEach(doc => {
			results.push({
				$id: doc.id,
				...doc.data(),
			})
		})

		return results
	}

	async updateBatch(data) {
		const batch = writeBatch(this.firestore)

		for (const path in data) {
			const update = data[path]
			const docRef = doc(this.firestore, path)
			batch.set(docRef, update, { merge: true })
		}

		const res = await batch.commit()
		return res
	}

	async readBatch(collectionPath, docIds, count = 30, keyed = false) {
		const collectionRef = collection(this.firestore, collectionPath)
		const batches = []
		for (let i = 0; i < docIds.length; i += count) {
			batches.push(docIds.slice(i, i + count))
		}

		const queryPromises = batches.map(batchIds => {
			const q = query(collectionRef, where(documentId(), 'in', batchIds))
			return getDocs(q)
		})

		const querySnapshots = await Promise.all(queryPromises)
		const documents = keyed ? {} : []
		querySnapshots.forEach(querySnapshot => {
			querySnapshot.forEach((doc) => {
				if (keyed) {
					documents[doc.id] = doc.data()
				} else {
					documents.push( doc.data() )
				}
			})
		})

		return documents
	}
}

Object.seal(FB)

export const fb = FB.getInstance()
